import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Reveals elements with a `.reveal` class inside the container on scroll.
 * Usage:
 *   const ref = useRevealOnScroll();
 *   return <div ref={ref}>...<h2 className="reveal">...</h2></div>
 */
export function useRevealOnScroll(options = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const ctx = gsap.context(() => {
      const items = root.querySelectorAll('.reveal');
      if (!items.length) return;

      gsap.fromTo(
        items,
        { y: 36, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: 'power3.out',
          stagger: options.stagger ?? 0.08,
          scrollTrigger: {
            trigger: root,
            start: options.start ?? 'top 78%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, root);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
}

/**
 * Parallax helper. Pass a ref and a strength (in px) and the element will
 * translateY based on its scroll progress.
 */
export function useParallax(strength = 80) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      gsap.to(el, {
        yPercent: -strength / 4,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    }, el);
    return () => ctx.revert();
  }, [strength]);

  return ref;
}

/**
 * Animated counter that counts to a target when scrolled into view.
 */
export function useCounter(target, duration = 2) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obj = { v: 0 };

    const ctx = gsap.context(() => {
      gsap.to(obj, {
        v: target,
        duration,
        ease: 'power2.out',
        onUpdate: () => {
          el.textContent = Math.floor(obj.v).toString();
        },
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      });
    }, el);

    return () => ctx.revert();
  }, [target, duration]);

  return ref;
}

/**
 * Magnetic button effect — the element follows the cursor slightly.
 */
export function useMagnetic(strength = 0.25) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const relX = e.clientX - (rect.left + rect.width / 2);
      const relY = e.clientY - (rect.top + rect.height / 2);
      gsap.to(el, { x: relX * strength, y: relY * strength, duration: 0.4, ease: 'power3.out' });
    };
    const onLeave = () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [strength]);

  return ref;
}

export { gsap, ScrollTrigger };
